import React, { FC, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { Card } from '~/components';
import { Flex, Inner, Text } from '~/styles';

import { CopyRow } from './CopyRow';

interface ForeignExchangeProps {
  data: {
    amount?: number | string | null;
    currencyCode?: string | null;
    instructedAmount?: number | string | null;
    instructedCurrencyCode?: string | null;
    settledAmount?: number | string | null;
    settledCurrencyCode?: string | null;
    fxRate?: number | string | null;
    fxQuoteId?: string | null;
    instructionToBeneficiaryFi?: string | null;
    chargeBearer?: string | null;
    uetr?: string | null;
  };
  isIncoming?: boolean;
}

const Wrapper = styled.div`
  display: grid;
  grid-gap: 24px;
  align-items: start;
  grid-template-columns: 25% auto 25%;
  padding: 0 24px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
  margin-bottom: 24px;
`;

const Title = styled.div`
  line-height: 1;
  display: grid;
  grid-gap: 4px;
`;

const Label = styled.div`
  margin-top: 12px;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};
`;

const StyledCard = styled(Card)`
  min-width: 248px;
`;

const CopyRowList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  gap: 0;

  li {
    border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

    &:first-child {
      border-top: none;
    }
  }
`;

const CustomField = styled(Flex)`
  padding: 16px;

  .chip {
    margin-top: -4px;
  }

  svg {
    --icon-size: 16px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  }
`;

const formatLabel = (content: string): string => {
  if (content === 'fxRate') return 'Foreign Exchange Rate';
  if (content === 'fxQuoteId') return 'Foreign Exchange Quote ID';

  const exceptions: { [key: string]: string } = {
    id: 'ID',
    iat: 'IAT',
    micr: 'MICR',
    fi: 'FI',
    di: 'DI',
    imad: 'IMAD',
    uetr: 'UETR',
    fx: 'FX',
    fis: 'FIS',
    omad: 'OMAD',
  };

  const words = content
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return exceptions[word] || word;
    });

  return words
    .join(' ')
    .replace(/\b\w/g, (s) => s.toUpperCase())
    .trim();
};

interface FieldProps {
  value: string | React.ReactNode;
  tooltip?: string;
  variant?: 'secondary' | 'default';
  isHidden?: boolean;
  isMonospaced?: boolean;
}

const getChargeBearerDescription = (value: string): string => {
  switch (value) {
    case 'SHAR':
      return 'Fees associated with this transfer are shared between the originator and beneficiary.';
    case 'DEBIT':
      return "Originator of this transfer is responsible for paying all fees associated with the transfer, including those charged by the sender's bank, the intermediary banks, and the receiving bank.";
    case 'CRED':
      return 'Beneficiary of this transfer is responsible for paying all fees associated with the transfer. This may result in the beneficiary receiving less than the full amount of the payment, as the beneficiary will need to cover any fees charged by intermediary banks and the receiving bank.';
    default:
      return '-';
  }
};

const getFieldProps = (data: ForeignExchangeProps['data'], isIncoming?: boolean): Record<string, FieldProps> => {
  const renderAmount = (amount: number | string | null | undefined, currencyCode: string | null | undefined) => {
    if (!amount) return '-';

    const formattedAmount = formatNumber(amount, currencyCode || 'USD');
    const CurrencyIcon =
      currencyCode && Icon.Currency[currencyCode as keyof typeof Icon.Currency]
        ? Icon.Currency[currencyCode as keyof typeof Icon.Currency]
        : Icon.Currency.USD;

    return (
      <CustomField gap={8} alignItems="center">
        <CurrencyIcon />
        <Flex gap={4} alignItems="center">
          <Text color="foreground" weight="medium">
            {formattedAmount}
          </Text>
          <Text color="light" weight="medium">
            {currencyCode || 'USD'}
          </Text>
        </Flex>
      </CustomField>
    );
  };

  const fields: Record<string, FieldProps> = {
    amount: {
      value: renderAmount(data.amount, data.currencyCode),
    },
    fxRate: {
      value: (
        <CustomField gap={8} alignItems="center">
          <Icon.ArrowExchange />
          <Text>{data.fxRate?.toString() || '1'}</Text>
        </CustomField>
      ),
      variant: 'secondary',
    },
    fxQuoteId: {
      value: data.fxQuoteId || '-',
      variant: 'secondary',
      isHidden: true,
      isMonospaced: true,
    },
    settledAmount: {
      value: renderAmount(data.settledAmount, data.settledCurrencyCode),
    },
    instructedAmount: {
      value: renderAmount(data.instructedAmount, data.instructedCurrencyCode),
      isHidden: !isIncoming,
    },
    instructionToBeneficiaryFi: {
      value: data.instructionToBeneficiaryFi || '-',
      isHidden: true,
    },
    chargeBearer: {
      value: data.chargeBearer ? (
        <CustomField flexDirection="column" gap={8}>
          <Chip size="large" className="chip">
            {data.chargeBearer}
          </Chip>
          <Text size={12} color="light">
            {getChargeBearerDescription(data.chargeBearer)}
          </Text>
        </CustomField>
      ) : (
        '-'
      ),
      tooltip: 'Party responsible for payment charges',
    },
    uetr: {
      value: data.uetr || '-',
      tooltip: 'Unique End-to-end Transaction Reference',
      isHidden: true,
      isMonospaced: true,
    },
  };

  const fieldOrder = isIncoming
    ? [
        'instructedAmount',
        'instructedCurrencyCode',
        'fxRate',
        'fxQuoteId',
        'settledAmount',
        'settledCurrencyCode',
        'instructionToBeneficiaryFi',
        'chargeBearer',
        'uetr',
      ]
    : [
        'amount',
        'fxRate',
        'fxQuoteId',
        'settledAmount',
        'instructedAmount',
        'instructionToBeneficiaryFi',
        'chargeBearer',
        'uetr',
      ];

  const orderedFields: Record<string, FieldProps> = {};
  fieldOrder.forEach((fieldName) => {
    if (fields[fieldName]) {
      orderedFields[fieldName] = fields[fieldName];
    }
  });

  return orderedFields;
};

const FieldVisibilityButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  cursor: pointer;
  font-size: 12px;

  &:hover {
    color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    max-height: 1000px;
  }
  to {
    opacity: 0;
    max-height: 0;
  }
`;

const AnimatedListItem = styled.li<{ isVisible: boolean }>`
  animation: ${({ isVisible }) => (isVisible ? fadeIn : fadeOut)} 0.3s ease-in-out forwards;
  overflow: hidden;
`;

const AnimatedField: FC<{ field: FieldProps; label: string; isVisible: boolean }> = ({ field, label, isVisible }) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <AnimatedListItem isVisible={isVisible}>
      <CopyRow
        label={label}
        value={field.value}
        tooltip={field.tooltip}
        variant={field.variant}
        isMonospaced={field.isMonospaced}
      />
    </AnimatedListItem>
  );
};

export const ForeignExchange: FC<ForeignExchangeProps> = ({ data, isIncoming }) => {
  const [showHiddenFields, setShowHiddenFields] = useState(false);
  const fieldProps = getFieldProps(data, isIncoming);
  const hasHiddenFields = Object.values(fieldProps).some((field) => field.isHidden);

  const toggleFieldVisibility = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowHiddenFields(!showHiddenFields);
  };

  return (
    <Wrapper>
      <Title>
        <Label>Foreign Exchange</Label>
      </Title>
      <StyledCard>
        <CopyRowList>
          {Object.entries(fieldProps).map(([key, field]) => (
            <AnimatedField
              key={key}
              field={field}
              label={formatLabel(key)}
              isVisible={!field.isHidden || showHiddenFields}
            />
          ))}
          <li>
            <Inner py={12} px={16}>
              <Flex alignItems="center">
                <Flex auto>
                  <Text as="p" size={12} color="lighter">
                    {isIncoming && 'Instructed'} Amount &times; Foreign Exchange Rate = Settled Amount
                  </Text>
                </Flex>

                {hasHiddenFields && (
                  <FieldVisibilityButton onClick={toggleFieldVisibility}>
                    {showHiddenFields ? 'Less details' : 'More details'}
                    {showHiddenFields ? <Icon.EyeClosed /> : <Icon.Eye />}
                  </FieldVisibilityButton>
                )}
              </Flex>
            </Inner>
          </li>
        </CopyRowList>
      </StyledCard>
    </Wrapper>
  );
};
